import React, { useMemo, ReactNode } from 'react';

import { FormattedMessage, InjectedIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';

import Avatar, { AvatarPropTypes as AvatarProps } from '@atlaskit/avatar';
import AvatarGroup from '@atlaskit/avatar-group';
import CommentIcon from '@atlaskit/icon/glyph/comment';
import { Box, Inline, Text } from '@atlaskit/primitives';

import { getAvatarUrl } from 'src/components/user-profile';
import { useIntl } from 'src/hooks/intl';
import { updateFilterCommentsByUser } from 'src/redux/pull-request/actions';
import { getIsCurrentSelectedFilter } from 'src/redux/pull-request/selectors';
import { getCurrentUser } from 'src/selectors/user-selectors';
import { publishUiEvent } from 'src/utils/analytics/publish';

import messages from './i18n';
import * as styles from './styles';

const AVATAR_GROUP_MAX_COUNT = 2;
const CommentBubble = <CommentIcon label="" />;

type AvatarGroupItem = AvatarProps & {
  ariaLabel: string;
  icon?: ReactNode;
  val: null | BB.User['uuid'];
  type: 'commentsByAllUsers' | 'commentsByCurrentUser'; // will eventually also include 'commentsBySpecificUser'
};

function CustomAvatar(props: AvatarGroupItem) {
  const { icon, val, type, ...avatarProps } = props;
  const selected = useSelector(s => getIsCurrentSelectedFilter(s, val));
  const dispatch = useDispatch();

  const onClickAvatar = () => {
    // dispatch action
    dispatch(updateFilterCommentsByUser(val));

    // send analytics event
    if (!selected) {
      publishUiEvent({
        action: 'clicked',
        actionSubject: 'button',
        actionSubjectId: 'pullRequestFileFilterButton',
        source: 'pullRequestScreen',
        attributes: { fileFilterState: type },
      });
    }
  };

  return (
    <Avatar {...avatarProps} stackIndex={selected ? AVATAR_GROUP_MAX_COUNT : 1}>
      {innerProps => (
        <styles.AvatarInnerButton
          aria-pressed={selected}
          aria-label={props.ariaLabel}
          onClick={onClickAvatar}
          className={innerProps.className}
        >
          {icon || innerProps.children}
        </styles.AvatarInnerButton>
      )}
    </Avatar>
  );
}

const MemoedCustomAvatar = React.memo(CustomAvatar);

export function DiffCommentFilters(_: {}): JSX.Element {
  const { formatMessage }: InjectedIntl = useIntl();
  const currentUser = useSelector(getCurrentUser);

  // // creates data object for AvatarGroup
  const groupData = useMemo(() => {
    // Avatar props for "All comments" option
    const allComments = {
      icon: CommentBubble,
      ariaLabel: formatMessage(messages.allCommentsAriaLabel),
      name: formatMessage(messages.allCommentsTooltip),
      type: 'commentsByAllUsers',
      val: null,
    };

    // Avatar props for "Your comments" option
    if (currentUser) {
      const yourComments = {
        ariaLabel: formatMessage(messages.yourCommentsAriaLabel),
        name: formatMessage(messages.yourCommentsTooltip),
        src: getAvatarUrl(currentUser),
        type: 'commentsByCurrentUser',
        val: currentUser.uuid,
      };

      return [allComments, yourComments];
    }
    return [allComments];
  }, [currentUser, formatMessage]);

  return (
    <Box paddingBlock="space.100">
      <Inline space="space.100" alignBlock="center">
        <Text color="color.text.subtlest">
          <FormattedMessage {...messages.filterLabel} />
        </Text>
        <AvatarGroup
          // @ts-ignore the BB MemoedCustomAvatar component expects an extra non-standard field "selected" to be supplied,
          // which the AvatarGroup component doesn't know about and therefore doesn't have in its types. This could be
          // refactored to avoid the type mismatch.
          avatar={MemoedCustomAvatar}
          data={groupData}
          testId="comment-filter"
          size="medium"
          maxCount={AVATAR_GROUP_MAX_COUNT}
        />
      </Inline>
    </Box>
  );
}

export default React.memo(DiffCommentFilters);
